import React, { useEffect, useMemo, useState, useRef } from "react";
import { CButton, CCard, CCardGroup, CCol, CForm, CFormLabel, CFormSwitch, CRow, CSpinner } from "@coreui/react";
import { dashboard, settings as settingsRoutes } from "@/libs/routes";
import { toast } from "react-toastify";
import { useTranslationContext } from "@/context/TranslationContext";
import { tab_key } from "@/constants/translation/common.translation";
import { useFormik } from "formik";
import CustomInput from "@/components/CustomInput";
import settings from "@/constants/translation/setting.translation";
import { useNavigate } from "react-router-dom";
import { edit_profile } from "@/constants/translation/auth.translation";
import { useGetSettingData, useUpdateSettingData } from "@/hooks";
import SettingValidationClass from "@/helpers/validations/setting";
import TabNavigation from "@/components/TabNavigation";
import constants from "@/libs/constant";

/**
 * @name AdminRewardsSettings
 *
 * @description
 * The AdminRewardsSettings component manages the admin-grant premium reward configuration:
 * how many months of free premium a grant gives, and whether new Normal User signups
 * automatically receive one. Lives under the "Rewards Settings" section alongside
 * Referral & Rewards, as its own independent subtab - separate from the referral milestone
 * settings and from General Settings.
 *
 * @returns {JSX.Element} The JSX for rendering the admin rewards settings form.
 */
const AdminRewardsSettings = () => {
  const navigate = useNavigate();
  const t = useTranslationContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionBlockRef = useRef(false);

  const mutation = useUpdateSettingData(
    (response) => {
      toast.success(response.message); // Show success message on successful update
      doGetSettingData(); // Refetch data to get updated settings
      setTimeout(() => {
        submissionBlockRef.current = false;
        setIsSubmitting(false); // Reset submission state
      }, 1000); // Short delay to prevent immediate re-submission
    },
    (error) => {
      toast.error(error.response?.data?.message || 'Failed to update settings');
      setTimeout(() => {
        submissionBlockRef.current = false;
        setIsSubmitting(false); // Reset submission state on error
      }, 1000); // Short delay to prevent immediate re-submission
    }
  );

  const { isPending } = mutation;

  const validationSchema = useMemo(
    () => SettingValidationClass.adminRewardsSettingValidation(t),
    [t]
  );

  const formik = useFormik({
    initialValues: {
      [constants.formFields.adminRewardsSettings.premiumGrantMonths]: "",
      [constants.formFields.adminRewardsSettings.premiumAutoGrantOnSignup]: false,
    },
    validationSchema,
    onSubmit: (values) => {
      if (submissionBlockRef.current || isSubmitting || isPending) {
        return; // Prevent multiple submissions
      }
      submissionBlockRef.current = true;
      setIsSubmitting(true);
      mutation.mutate(values);
    },
  });

  /**
   * !This API will call while Page Load and set data
   */
  const {
    refetch: doGetSettingData,
    isLoading: isLoadingData,
    data: getConfiguration,
  } = useGetSettingData();
  useEffect(() => {
    if (!isLoadingData && getConfiguration && getConfiguration.data) {
      const data = getConfiguration.data;

      formik.setValues({
        [constants.formFields.adminRewardsSettings.premiumGrantMonths]: data.premium_grant_months || "",
        [constants.formFields.adminRewardsSettings.premiumAutoGrantOnSignup]:
          data.premium_auto_grant_on_signup === "true" || data.premium_auto_grant_on_signup === true,
      });
    }
  }, [getConfiguration, isLoadingData]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Guards turning the auto-grant toggle on: the months field must already hold a
   * valid positive integer, otherwise the switch is left off and the admin is told
   * to configure the months field first. Turning the toggle off is always allowed.
   */
  const handlePremiumAutoGrantToggle = (checked) => {
    const monthsField = constants.formFields.adminRewardsSettings.premiumGrantMonths;
    const monthsValue = formik.values[monthsField];
    const numericMonths = Number(monthsValue);
    const isValidMonths =
      monthsValue !== "" &&
      monthsValue !== null &&
      monthsValue !== undefined &&
      Number.isInteger(numericMonths) &&
      numericMonths >= constants.validation.adminRewardsSettings.premiumGrantMonthsMin;

    if (checked && !isValidMonths) {
      toast.error(t(settings.general_settings.premium_auto_grant_blocked));
      return;
    }

    formik.setFieldValue(constants.formFields.adminRewardsSettings.premiumAutoGrantOnSignup, checked);
  };

  const tabs = [
    {
      label: t(tab_key.settings.referral_settings),
      path: settingsRoutes.referral,
    },
    {
      label: t(tab_key.settings.admin_rewards_settings),
      path: '',
    },
  ]

  return (
    <>
      <CCardGroup>
        <CCard className="p-4">
          <TabNavigation tabs={tabs} />
          {!isLoadingData && (
            <>
              <CForm onSubmit={formik.handleSubmit}>
                <CRow>
                  <CCol lg={6} md={6} sm={12}>
                    <CustomInput
                      label={t(settings.general_settings.premium_grant_months_label)}
                      type="number"
                      name={constants.formFields.adminRewardsSettings.premiumGrantMonths}
                      min={constants.validation.adminRewardsSettings.premiumGrantMonthsMin}
                      value={formik.values[constants.formFields.adminRewardsSettings.premiumGrantMonths]}
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      error={
                        formik.touched[constants.formFields.adminRewardsSettings.premiumGrantMonths] &&
                        formik.errors[constants.formFields.adminRewardsSettings.premiumGrantMonths]
                      }
                    />
                  </CCol>
                  <CCol lg={6} md={6} sm={12}>
                    <CFormLabel>{t(settings.general_settings.premium_auto_grant_label)}</CFormLabel>
                    <CFormSwitch
                      id={constants.formFields.adminRewardsSettings.premiumAutoGrantOnSignup}
                      name={constants.formFields.adminRewardsSettings.premiumAutoGrantOnSignup}
                      label={
                        formik.values[constants.formFields.adminRewardsSettings.premiumAutoGrantOnSignup]
                          ? t(settings.general_settings.premium_auto_grant_active)
                          : t(settings.general_settings.premium_auto_grant_inactive)
                      }
                      checked={formik.values[constants.formFields.adminRewardsSettings.premiumAutoGrantOnSignup]}
                      onChange={(e) => handlePremiumAutoGrantToggle(e.target.checked)}
                    />
                    {formik.touched[constants.formFields.adminRewardsSettings.premiumAutoGrantOnSignup] &&
                      formik.errors[constants.formFields.adminRewardsSettings.premiumAutoGrantOnSignup] && (
                        <div className="text-danger small mt-1">
                          {formik.errors[constants.formFields.adminRewardsSettings.premiumAutoGrantOnSignup]}
                        </div>
                      )}
                  </CCol>
                </CRow>
                <CRow>
                  <CCol className="d-flex justify-content-center mt-2 gap-2">
                    <CButton
                      color="primary"
                      className="px-4"
                      type="submit"
                      disabled={isPending || isSubmitting || submissionBlockRef.current}
                    >
                      {(isPending || isSubmitting || submissionBlockRef.current) && <CSpinner size="sm" variant="grow" />}
                      {t(edit_profile.submit_btn_text)}
                    </CButton>
                    <CButton
                      color="secondary"
                      className="px-4"
                      type="button"
                      disabled={isPending || isSubmitting || submissionBlockRef.current}
                      onClick={() => {
                        navigate(dashboard);
                      }}
                    >
                      {t(edit_profile.cancel_button)}
                    </CButton>
                  </CCol>
                </CRow>
              </CForm>
            </>
          )}
        </CCard>
      </CCardGroup>
    </>
  );
};

export default AdminRewardsSettings;
